import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { FilePlus2, Layers, Rocket, Search, type LucideIcon } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Skeleton, StatCard, notifySaved } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../../app/console-context";
import { keyFromLabel, useDefinitionActions, useDefinitionList, type DefinitionSummary, type GenericResource } from "./api";

export interface CatalogColumn<S> { header: string; cell: (row: S) => ReactNode }

interface Props<S extends DefinitionSummary, B> {
  resource: GenericResource;
  basePath: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  navLabel: string;
  writePermission: string;
  newLabel: string;
  emptyTitle: string;
  emptyBody: string;
  help: ReactNode;
  docsAnchor: string;
  columns?: CatalogColumn<S>[];
  /** Extra stat cards after the total/published/draft ones. */
  stats?: (rows: S[]) => ReactNode;
  /** The body of version 1 for a new key; `extra` holds what the create dialog's extra fields collected. */
  newBody: (name: string, nameAr: string | null, extra: Record<string, string>) => B;
  /** Extra fields in the create dialog (e.g. a tool's kind); `extra.__name` carries the name typed so far. */
  createFields?: (extra: Record<string, string>, set: (k: string, v: string) => void) => ReactNode;
  /** Actions next to "New" in the header. */
  headerActions?: ReactNode;
  /** Content under the table (e.g. a gallery of templates). */
  footer?: ReactNode;
}

/** The catalogue page every generic-model designer shares: stats, search, status filter, the table of keys and a create dialog. */
export function CatalogPage<S extends DefinitionSummary, B>(props: Props<S, B>) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can(props.writePermission);
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const list = useDefinitionList<S>(props.resource, { q: q || undefined, status: status || undefined });
  const rows = list.data ?? [];

  useBreadcrumbs(useMemo(() => [{ label: props.navLabel }], [props.navLabel]));
  useHelpTopic({ title: props.title, docsAnchor: props.docsAnchor, body: props.help });
  usePaletteActions(useMemo(() => canWrite ? [{ id: `${props.resource}.new`, label: props.newLabel, icon: FilePlus2, permission: props.writePermission, run: () => setCreating(true) }] : [], [canWrite, props.newLabel, props.resource, props.writePermission]));

  const open = (key: string) => navigate(`${props.basePath}/${encodeURIComponent(key)}`);

  return (
    <div>
      <PageHeader title={props.title} description={props.subtitle} actions={<div className="flex gap-2">{props.headerActions}{canWrite ? <Button variant="gold" onClick={() => setCreating(true)}><FilePlus2 /> {props.newLabel}</Button> : null}</div>} />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:generic.stats.total")} value={rows.length} icon={props.icon} tone="gold" footer={t("admin:generic.stats.totalHint")} />
        <StatCard label={t("admin:generic.stats.published")} value={rows.filter((r) => r.publishedVersion != null).length} icon={Rocket} tone="sea" footer={t("admin:generic.stats.publishedHint")} />
        <StatCard label={t("admin:generic.stats.drafts")} value={rows.filter((r) => r.draftVersion != null).length} icon={Layers} tone="camel" footer={t("admin:generic.stats.draftsHint")} />
        {props.stats?.(rows)}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:generic.search")} className="h-9 w-64 ps-8" /></div>
        <NativeSelect value={status} className="h-9 w-44" onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("admin:common.status")}: {t("admin:common.all")}</option>
          <option value="published">{t("admin:forms.filters.published")}</option>
          <option value="draft">{t("admin:forms.filters.hasDraft")}</option>
          <option value="unpublished">{t("admin:generic.filters.unpublished")}</option>
        </NativeSelect>
      </div>

      {list.isLoading ? <div className="space-y-2"><Skeleton className="h-14" /><Skeleton className="h-14" /></div> : rows.length === 0 ? (
        <EmptyState icon={props.icon} title={q || status ? t("admin:generic.noMatch") : props.emptyTitle} description={q || status ? undefined : props.emptyBody}
          action={canWrite && !q && !status ? <Button variant="gold" onClick={() => setCreating(true)}><FilePlus2 /> {props.newLabel}</Button> : undefined} />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-sand-200 bg-white/80">
          <table className="w-full text-sm">
            <thead className="bg-sand-50 text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-start font-medium">{t("admin:common.name")}</th>
                {(props.columns ?? []).map((c) => <th key={c.header} className="px-3 py-2 text-start font-medium">{c.header}</th>)}
                <th className="px-3 py-2 text-start font-medium">{t("admin:forms.columns.published")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("admin:forms.columns.draft")}</th>
                <th className="px-3 py-2 text-start font-medium">{t("admin:common.updated")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-sand-100">
              {rows.map((r) => (
                <tr key={r.key} data-testid={`row-${r.key}`} className="cursor-pointer transition-colors hover:bg-aegold-50/40" tabIndex={0} onClick={() => open(r.key)} onKeyDown={(e) => { if (e.key === "Enter") open(r.key); }}>
                  <td className="px-4 py-2.5"><div className="font-medium">{locale === "ar" && r.nameAr ? r.nameAr : r.name}</div><div className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground"><code className="font-mono" dir="ltr">{r.key}</code>{r.tags.slice(0, 3).map((tg) => <Badge key={tg} variant="outline" size="sm">{tg}</Badge>)}{r.sourcePackageKey ? <Badge variant="camel" size="sm">{r.sourcePackageKey}</Badge> : null}</div></td>
                  {(props.columns ?? []).map((c) => <td key={c.header} className="px-3 py-2.5 text-xs">{c.cell(r)}</td>)}
                  <td className="px-3 py-2.5">{r.publishedVersion != null ? <Badge variant="success" size="sm" dot>v{r.publishedVersion}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2.5">{r.draftVersion != null ? <Badge variant={r.draftStatus === "InReview" ? "warning" : "info"} size="sm" dot>v{r.draftVersion} · {t(`admin:forms.status.${r.draftStatus ?? "Draft"}`)}</Badge> : <span className="text-xs text-muted-foreground">—</span>}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{formatDateTime(r.updatedAt, locale)}{r.updatedByName ? ` · ${r.updatedByName}` : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {props.footer}

      <CreateDialog open={creating} onOpenChange={setCreating} existing={rows.map((r) => r.key)} resource={props.resource} title={props.newLabel} newBody={props.newBody} createFields={props.createFields} onCreated={open} />
    </div>
  );
}

function CreateDialog<B>({ open, onOpenChange, existing, resource, title, newBody, createFields, onCreated }: {
  open: boolean; onOpenChange: (o: boolean) => void; existing: string[]; resource: GenericResource; title: string;
  newBody: (name: string, nameAr: string | null, extra: Record<string, string>) => B;
  createFields?: (extra: Record<string, string>, set: (k: string, v: string) => void) => ReactNode;
  onCreated: (key: string) => void;
}) {
  const { t } = useTranslation();
  const actions = useDefinitionActions<B>(resource);
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [touched, setTouched] = useState(false);
  const [extra, setExtra] = useState<Record<string, string>>({});
  const effectiveKey = touched ? key : keyFromLabel(name);
  const keyError = !effectiveKey ? null : !/^[a-z][a-z0-9._-]{1,63}$/.test(effectiveKey) ? t("admin:forms.create.keyInvalid") : existing.includes(effectiveKey) ? t("admin:forms.create.keyTaken") : null;
  const reset = () => { setName(""); setNameAr(""); setKey(""); setTouched(false); setExtra({}); };
  const create = async () => {
    try {
      const d = await actions.create.mutateAsync({ key: effectiveKey, body: newBody(name.trim(), nameAr.trim() || null, extra) });
      notifySaved(d, t("admin:generic.created"));
      onOpenChange(false); reset(); onCreated(d.key);
    } catch { /* useSave reported it */ }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{t("admin:generic.createIntro")}</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Field label={t("admin:common.name")} required><Input data-testid="create-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus /></Field>
          <Field label={t("admin:common.nameAr")}><Input data-testid="create-name-ar" value={nameAr} dir="rtl" onChange={(e) => setNameAr(e.target.value)} /></Field>
          <Field label={t("admin:common.key")} hint={t("admin:generic.keyHint")} error={keyError ?? undefined}><Input data-testid="create-key" value={effectiveKey} dir="ltr" className="font-mono" onChange={(e) => { setTouched(true); setKey(e.target.value.toLowerCase()); }} /></Field>
          {createFields?.({ ...extra, __name: name }, (k, v) => setExtra((x) => ({ ...x, [k]: v })))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button data-testid="create-submit" variant="gold" disabled={name.trim().length < 2 || !effectiveKey || !!keyError} loading={actions.create.isPending} onClick={create}><FilePlus2 /> {t("admin:generic.createOpen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
